import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Trophy, MessageSquare, Mail, Share2, Facebook, Twitter, Copy, X, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { useAuth } from '../contexts/AuthContext';

// WhatsApp brand icon (lucide doesn't ship one)
const WhatsAppIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z" />
    </svg>
);

const SEEN_KEY = (entryId) => `lucky_pack_share_seen_${entryId}`;

/**
 * Win Share Dialog — auto-opens once when a player wins 4/5 or 5/5 on
 * Lucky 5. Pre-fills a celebratory message with the player's referral
 * link, so each win becomes a frictionless social-proof moment.
 *
 * Dismissal is persisted per entry id in localStorage — no repeated prompts
 * after they close it.
 */
const LuckyPackWinShareDialog = ({ myEntry }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [open, setOpen] = useState(false);

    // Only fire for "real" wins (4/5 or 5/5). Refund (3/5) is a freebie —
    // not exciting enough to justify a popup interruption.
    const isBigWin = myEntry
        && myEntry.status === 'won'
        && (myEntry.hits_count || 0) >= 4
        && (myEntry.payout_amount || 0) > 0;

    const entryId = myEntry?.id || '';

    useEffect(() => {
        if (!isBigWin || !entryId) return;
        try {
            if (window.localStorage.getItem(SEEN_KEY(entryId))) return;
        } catch { /* ignore */ }
        // Brief delay so the lobby has time to render around the celebration
        const id = setTimeout(() => setOpen(true), 600);
        return () => clearTimeout(id);
    }, [isBigWin, entryId]);

    const close = () => {
        setOpen(false);
        try {
            if (entryId) window.localStorage.setItem(SEEN_KEY(entryId), '1');
        } catch { /* ignore */ }
    };

    const referralLink = useMemo(() => {
        if (!user?.referral_code) return '';
        return `${window.location.origin}/api/r/${user.referral_code}`;
    }, [user?.referral_code]);

    const amount = Number(myEntry?.payout_amount || 0).toFixed(0);
    const hits = myEntry?.hits_count || 0;

    const shareText = useMemo(() => {
        const headline = t('lucky_pack.share_text', { amount, hits });
        return referralLink ? `${headline} ${referralLink}` : headline;
    }, [t, amount, hits, referralLink]);

    const copyText = async () => {
        try {
            await navigator.clipboard.writeText(shareText);
            toast.success(t('lucky_pack.share_copied'));
        } catch {
            toast.error('Could not copy');
        }
    };

    const nativeShare = async () => {
        if (!navigator.share) return;
        try {
            await navigator.share({
                title: 'BETohalics — Lucky 5',
                text: t('lucky_pack.share_text', { amount, hits }),
                url: referralLink || window.location.origin,
            });
        } catch { /* user cancelled */ }
    };

    const hasNativeShare = typeof navigator !== 'undefined' && !!navigator.share;
    const channels = [
        { key: 'sms', label: t('lucky_pack.share_sms'), icon: MessageSquare, color: '#39FF14',
            href: `sms:?&body=${encodeURIComponent(shareText)}` },
        { key: 'whatsapp', label: 'WhatsApp', icon: WhatsAppIcon, color: '#25D366',
            href: `https://wa.me/?text=${encodeURIComponent(shareText)}`, target: '_blank' },
        { key: 'email', label: t('lucky_pack.share_email'), icon: Mail, color: '#FFD700',
            href: `mailto:?subject=${encodeURIComponent('I just won on BETohalics Lucky 5!')}&body=${encodeURIComponent(shareText)}` },
        { key: 'facebook', label: 'Facebook', icon: Facebook, color: '#1877F2',
            href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink || window.location.origin)}&quote=${encodeURIComponent(shareText)}`, target: '_blank' },
        { key: 'twitter', label: 'X', icon: Twitter, color: '#FFFFFF',
            href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`, target: '_blank' },
    ];

    if (!isBigWin) return null;

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
            <DialogContent
                className="bg-[#0a0a0a] border-[#FFD700]/40 text-white max-w-md p-0 overflow-hidden"
                data-testid="lucky-pack-win-share-dialog"
            >
                {/* Confetti / gradient header */}
                <div
                    className="relative p-5 pb-4"
                    style={{
                        background: 'linear-gradient(135deg, rgba(57,255,20,0.18) 0%, rgba(255,215,0,0.25) 50%, rgba(255,0,127,0.18) 100%)',
                        borderBottom: '1px solid rgba(255,215,0,0.3)',
                    }}
                >
                    <motion.div
                        initial={{ scale: 0.5, rotate: -15, opacity: 0 }}
                        animate={{ scale: 1, rotate: 0, opacity: 1 }}
                        transition={{ duration: 0.4, ease: 'backOut' }}
                        className="flex items-center gap-3 mb-2"
                    >
                        <div className="w-12 h-12 rounded-full bg-[#FFD700]/20 border border-[#FFD700]/50 flex items-center justify-center">
                            <Trophy className="w-6 h-6 text-[#FFD700]" />
                        </div>
                        <div className="flex-1">
                            <DialogHeader className="space-y-0">
                                <DialogTitle className="text-white text-2xl font-black leading-tight">
                                    {t('lucky_pack.share_headline', { amount })}
                                </DialogTitle>
                            </DialogHeader>
                            <p className="text-[#FFD700] text-sm font-bold mt-0.5">
                                {hits === 5
                                    ? t('lucky_pack.share_subhead_perfect')
                                    : t('lucky_pack.share_subhead_four', { hits })}
                            </p>
                        </div>
                    </motion.div>
                    <DialogDescription className="text-[#A0AAB2] text-sm">
                        <Sparkles className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5 text-[#FFD700]" />
                        {t('lucky_pack.share_brag_blurb')}
                    </DialogDescription>
                </div>

                {/* Pre-filled message preview */}
                <div className="px-5 pt-4">
                    <p className="text-[10px] uppercase tracking-wider text-[#A0AAB2] mb-1 font-bold">
                        {t('lucky_pack.share_message_label')}
                    </p>
                    <div className="rounded-lg bg-[#0e0e0e] border border-white/10 p-3 text-xs text-white leading-relaxed">
                        {shareText}
                    </div>
                    <button
                        type="button"
                        onClick={copyText}
                        className="mt-2 w-full flex items-center justify-center gap-2 text-xs font-bold text-[#FFD700] hover:text-white border border-[#FFD700]/30 hover:border-[#FFD700]/60 rounded-lg py-2 transition-colors"
                        data-testid="lucky-share-copy-btn"
                    >
                        <Copy className="w-3.5 h-3.5" /> {t('lucky_pack.share_copy')}
                    </button>
                </div>

                {/* Channel grid */}
                <div className="px-5 pt-3 pb-5">
                    <p className="text-[10px] uppercase tracking-wider text-[#A0AAB2] mb-2 font-bold">
                        {t('lucky_pack.share_channels_label')}
                    </p>
                    <div className="grid grid-cols-3 sm:grid-cols-3 gap-2">
                        {channels.map((c) => {
                            const Icon = c.icon;
                            return (
                                <a
                                    key={c.key}
                                    href={c.href}
                                    target={c.target}
                                    rel={c.target === '_blank' ? 'noopener noreferrer' : undefined}
                                    onClick={() => {
                                        // Mark seen — they engaged.
                                        try { window.localStorage.setItem(SEEN_KEY(entryId), '1'); } catch { /* ignore */ }
                                    }}
                                    className="group flex flex-col items-center justify-center gap-1.5 p-3 rounded-lg bg-[#0e0e0e] border border-white/10 hover:border-white/30 hover:bg-white/5 transition-colors"
                                    data-testid={`lucky-share-${c.key}`}
                                >
                                    <Icon className="w-5 h-5" style={{ color: c.color }} />
                                    <span className="text-[10px] text-[#A0AAB2] group-hover:text-white transition-colors">
                                        {c.label}
                                    </span>
                                </a>
                            );
                        })}
                        {hasNativeShare && (
                            <button
                                type="button"
                                onClick={nativeShare}
                                className="group flex flex-col items-center justify-center gap-1.5 p-3 rounded-lg bg-[#0e0e0e] border border-white/10 hover:border-white/30 hover:bg-white/5 transition-colors"
                                data-testid="lucky-share-native"
                            >
                                <Share2 className="w-5 h-5 text-[#00FFFF]" />
                                <span className="text-[10px] text-[#A0AAB2] group-hover:text-white transition-colors">
                                    {t('lucky_pack.share_more')}
                                </span>
                            </button>
                        )}
                    </div>

                    <button
                        type="button"
                        onClick={close}
                        className="mt-4 w-full text-center text-xs text-[#A0AAB2] hover:text-white py-2"
                        data-testid="lucky-share-later-btn"
                    >
                        <X className="w-3 h-3 inline-block mr-1" /> {t('lucky_pack.share_later')}
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default LuckyPackWinShareDialog;
